import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { HubIcon } from '@/components/Hub/HubIcon';
import { HubSectionSlot } from '@/components/Hub/HubSectionSlot';
import { HUB_SECTIONS } from '@/components/Hub/hubSections';
import { HUB_DARK_TEXT_CLASS } from '@/components/Hub/hubTheme';
import {
    skillPanelSheetBodyClass,
    skillPanelSheetHeaderClass,
    skillPanelSheetTitleClass,
} from '@/pages/LegacySkills/skillPanelSheetLayout';
import { cn } from '@/lib/utils';

export interface HubPanelProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const HubPanel = ({ open, onOpenChange }: HubPanelProps) => {
    const isDesktop = useIsDesktop();
    const side = isDesktop ? 'right' : 'bottom';
    const location = useLocation();

    /**
     * Which sections found something to show. A section still loading counts as content, so the
     * empty message appears only once every one of them has come back with nothing.
     */
    const [hasContentById, setHasContentById] = useState<Record<string, boolean>>({});
    const isEmpty = HUB_SECTIONS.every(section => hasContentById[section.id] === false);

    const onHasContentChange = useCallback((id: string, hasContent: boolean) => {
        setHasContentById(current =>
            (current[id] === hasContent ? current : { ...current, [id]: hasContent }));
    }, []);

    // Closing unmounts the sections so their answers are cleared with them. Stale ones would
    // show the empty message for a frame the next time the panel opens with real content.
    useEffect(() => {
        if (!open) {
            setHasContentById({});
        }
    }, [open]);

    // Following a link out of the Hub should leave it closed behind you.
    useEffect(() => {
        onOpenChange(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side={side}
                className={cn(
                    'flex flex-col border-none bg-secondary light:bg-background dark:bg-gz-blue-800',
                    side === 'right'
                        ? 'h-full w-full rounded-tl-xl rounded-bl-xl sm:max-w-[600px]'
                        : 'max-h-[90dvh] rounded-t-xl',
                )}
            >
                <SheetHeader
                    className={cn(
                        skillPanelSheetHeaderClass,
                        // Margin rather than padding so the rule stops short of the sheet edges while
                        // the title stays where the shared header class puts it.
                        'mx-6 px-0 border-b pb-4',
                        // Dark mode does not reuse the light divider color, which glares against
                        // the dark sheet at full strength.
                        'light:border-gz-neutral-200 dark:border-white/15',
                    )}
                >
                    <SheetTitle
                        className={cn(
                            skillPanelSheetTitleClass,
                            'type-headline-small flex items-center gap-3',
                            HUB_DARK_TEXT_CLASS,
                        )}
                    >
                        {/* The icon assets are bare glyphs, so the backing disc comes from here.
                            Clipped because a transition overflows this box with the disc it paints
                            the button face with, and padded so the glyph keeps a rim inside it. */}
                        <span className="size-12 shrink-0 overflow-hidden rounded-full p-1.5 dark:bg-gz-marine-500">
                            <HubIcon />
                        </span>
                        Hub
                    </SheetTitle>
                    {/* The header carries only the icon, the name and the close button, but Radix
                        requires a description, so this one is read rather than shown. */}
                    <SheetDescription className="sr-only">
                        Stories and resources picked for where you are right now.
                    </SheetDescription>
                </SheetHeader>
                <div className={cn(skillPanelSheetBodyClass, HUB_DARK_TEXT_CLASS, 'gap-6 pt-2')}>
                    {HUB_SECTIONS.map(section => (
                        <HubSectionSlot
                            key={section.id}
                            section={section}
                            onHasContentChange={onHasContentChange}
                        />
                    ))}
                    {isEmpty && (
                        <p className="py-8 text-center type-body-default text-muted-foreground">
                            Nothing to show here yet. Stories follow the career you are looking at or
                            the journeys you have started, and the Library fills in as articles are
                            published.
                        </p>
                    )}
                </div>
            </SheetContent>
        </Sheet>
    );
};
